import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, ChevronDown, Loader2, Monitor, Smartphone, Tablet } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Browser-Push channel card for the Customer Detail dialog.
 *
 * Shows: active device count, last push at, last seen at — plus an
 * expandable list of every device (browser, type, status, last seen).
 *
 * Renders a muted "0 devices" card when the customer never subscribed,
 * NOT a hidden block — merchants want to see channel reachability per
 * customer at a glance, including absence.
 */

function _relative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  if (diff < 30 * 86_400_000) return `${Math.round(diff / 86_400_000)} d ago`;
  return new Date(iso).toLocaleDateString();
}

function _DeviceIcon({ deviceType, className }) {
  if (deviceType === 'mobile') return <Smartphone className={className} />;
  if (deviceType === 'tablet') return <Tablet className={className} />;
  return <Monitor className={className} />;
}

const STATUS_STYLES = {
  active: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  expired: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  unsubscribed: 'text-gray-400 bg-gray-500/10 border-gray-500/30',
};

export default function BrowserPushCard({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE_URL}/push/customers/${customerId}/devices`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load push devices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading && !data) {
    return (
      <div className="bg-wt-bg rounded-lg p-4 col-span-2" data-testid="browser-push-card-loading">
        <div className="flex items-center gap-2 text-wt-text-muted text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading push devices…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-wt-bg rounded-lg p-4 col-span-2 text-sm text-red-300" data-testid="browser-push-card-error">
        {error}
      </div>
    );
  }

  const activeCount = data?.active_count ?? 0;
  const totalCount = data?.total_count ?? 0;
  const devices = data?.devices ?? [];

  return (
    <div
      className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-lg p-4 col-span-2 border border-amber-500/20"
      data-testid="browser-push-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-amber-400 text-sm font-medium flex items-center gap-1.5">
            <Bell className="h-4 w-4" />
            Browser Push
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span
              className="text-2xl font-bold text-amber-400"
              data-testid="browser-push-active-count"
            >
              {activeCount}
            </span>
            <span className="text-xs text-wt-text-muted">
              active device{activeCount === 1 ? '' : 's'}
              {totalCount > activeCount && ` · ${totalCount - activeCount} inactive`}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
            <span className="text-wt-text-muted">Last push</span>
            <span className="text-wt-text-secondary text-right" data-testid="browser-push-last-pushed">
              {_relative(data?.last_pushed_at)}
            </span>
            <span className="text-wt-text-muted">Last seen</span>
            <span className="text-wt-text-secondary text-right" data-testid="browser-push-last-seen">
              {_relative(data?.last_seen_at)}
            </span>
          </div>
        </div>
        {devices.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex-shrink-0 text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 mt-1"
            data-testid="browser-push-toggle-devices"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide' : 'View'} devices
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {expanded && devices.length > 0 && (
        <div
          className="mt-3 space-y-1.5 border-t border-amber-500/20 pt-3"
          data-testid="browser-push-device-list"
        >
          {devices.map((d, i) => {
            const status = d.status || 'active';
            return (
              <div
                key={d.id || i}
                className="flex items-center gap-3 bg-wt-bg/40 rounded-md px-2.5 py-1.5 text-xs"
                data-testid={`browser-push-device-${i}`}
              >
                <_DeviceIcon
                  deviceType={d.device_type}
                  className="h-4 w-4 text-amber-400 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-wt-text-primary capitalize truncate">
                    {(d.browser || 'Browser') + ' · ' + (d.device_type || 'desktop')}
                  </p>
                  <p className="text-wt-text-muted truncate">
                    Subscribed {_relative(d.created_at)} · seen {_relative(d.last_seen_at)}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide flex-shrink-0 ${STATUS_STYLES[status] || STATUS_STYLES.unsubscribed}`}
                >
                  {status}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
